import 'react-native-get-random-values';
import 'react-native-url-polyfill/auto';
import Ionicons from '@expo/vector-icons/Ionicons';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import * as Sentry from '@sentry/react-native';
import { useFonts } from 'expo-font';
import {
  DarkTheme,
  DefaultTheme,
  Slot,
  ThemeProvider,
  useNavigationContainerRef,
} from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import { useEffect } from 'react';
import { Platform, Pressable, Text, View } from 'react-native';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import RootGestureWrapper from '@/components/Navigation/RootGestureWrapper';
import useInitializeTheme from '@/hooks/useInitializeTheme';
import '@/styles/rich-text.css';
import useThemeStore from '@/stores/useThemeStore';
import connectToFirebase from '@/utils/connectToFirebase';
import isDesktopUserAgent from '@/utils/isDesktopUserAgent';
import {
  isJoinTelemetryRecord,
  redactJoinTelemetryDataInPlace,
  redactJoinTelemetryText,
} from '@/utils/redactJoinTelemetry';

SplashScreen.preventAutoHideAsync();

const NavigationThemeProvider = withUnistyles(
  ThemeProvider,
  (theme, runtime) => ({
    value: {
      ...(runtime.themeName === 'dark' ? DarkTheme : DefaultTheme),
      colors: {
        primary: theme.colors.primary,
        background: theme.colors.surface,
        card: theme.colors.surface,
        text: theme.colors.typography,
        border: theme.colors.border,
        notification: theme.colors.error,
      },
    },
  }),
);

connectToFirebase(process.env.EXPO_PUBLIC_ENV_NAME === 'dev');

if (isDesktopUserAgent && process.env.EXPO_PUBLIC_ENV_NAME !== 'dev') {
  window.addEventListener(
    'contextmenu',
    (e) => {
      e.preventDefault();
    },
    false,
  );
}

const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: true,
});

function redactSentryEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  for (const breadcrumb of event.breadcrumbs ?? []) {
    redactJoinTelemetryDataInPlace(breadcrumb.data);
    if (breadcrumb.message) {
      breadcrumb.message = redactJoinTelemetryText(breadcrumb.message);
    }
  }

  redactJoinTelemetryDataInPlace(event.contexts);
  redactJoinTelemetryDataInPlace(event.exception);
  redactJoinTelemetryDataInPlace(event.extra);
  redactJoinTelemetryDataInPlace(event.tags);
  redactJoinTelemetryDataInPlace(event.user);

  if (event.message) {
    event.message = redactJoinTelemetryText(event.message);
  }

  if (event.request) {
    if (isJoinTelemetryRecord(event.request.data)) {
      redactJoinTelemetryDataInPlace(event.request.data);
    } else if (typeof event.request.data === 'string') {
      event.request.data = redactJoinTelemetryText(event.request.data);
    }

    if (event.request.url) {
      event.request.url = redactJoinTelemetryText(event.request.url);
    }

    if (typeof event.request.query_string === 'string') {
      event.request.query_string = redactJoinTelemetryText(
        event.request.query_string,
      );
    } else {
      redactJoinTelemetryDataInPlace(event.request.query_string);
    }
  }

  if (event.transaction) {
    event.transaction = redactJoinTelemetryText(event.transaction);
  }

  return event;
}

Sentry.init({
  beforeBreadcrumb: (breadcrumb) => {
    redactJoinTelemetryDataInPlace(breadcrumb.data);
    if (breadcrumb.message) {
      breadcrumb.message = redactJoinTelemetryText(breadcrumb.message);
    }

    return breadcrumb;
  },
  beforeSend: redactSentryEvent,
  dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
  integrations: [navigationIntegration],
  environment: process.env.EXPO_PUBLIC_ENV_NAME,
  enabled: process.env.EXPO_PUBLIC_ENV_NAME !== 'dev',
  enableNativeFramesTracking: true,
});

type RootErrorFallbackProps = {
  resetError(): void;
};

function RootErrorFallback({ resetError }: RootErrorFallbackProps) {
  const recover = () => {
    if (Platform.OS === 'web') {
      window.location.reload();
      return;
    }

    resetError();
  };

  return (
    <View style={styles.errorFallback}>
      <Text style={styles.errorTitle}>Something went wrong</Text>
      <Text style={styles.errorMessage}>
        We’ve been notified. Try again to continue.
      </Text>
      <Pressable
        accessibilityRole="button"
        onPress={recover}
        style={styles.errorButton}
      >
        <Text style={styles.errorButtonText}>Try again</Text>
      </Pressable>
    </View>
  );
}

function RootLayout() {
  useInitializeTheme();
  const realTheme = useThemeStore((state) => state.realTheme);
  const [fontsLoaded, fontError] = useFonts({
    ...Ionicons.font,
    ...MaterialCommunityIcons.font,
  });

  useEffect(() => {
    if (fontsLoaded || fontError) {
      SplashScreen.hideAsync();
    }
  }, [fontsLoaded, fontError]);

  const ref = useNavigationContainerRef();

  useEffect(() => {
    if (ref.current) {
      navigationIntegration.registerNavigationContainer(ref);
    }
  }, [ref]);

  if (!fontsLoaded && !fontError) {
    return null;
  }

  return (
    <KeyboardProvider>
      <RootGestureWrapper>
        <NavigationThemeProvider>
          <Slot />
        </NavigationThemeProvider>
        <StatusBar style={realTheme === 'dark' ? 'light' : 'dark'} animated />
      </RootGestureWrapper>
    </KeyboardProvider>
  );
}

export default function RootLayoutRoute() {
  return (
    <Sentry.ErrorBoundary fallback={RootErrorFallback} handled={false}>
      <RootLayout />
    </Sentry.ErrorBoundary>
  );
}

const styles = StyleSheet.create((theme) => ({
  errorFallback: {
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    flex: 1,
    gap: theme.space('xl'),
    justifyContent: 'center',
    padding: theme.space('4xl'),
  },
  errorTitle: {
    color: theme.colors.typography,
    fontSize: theme.fontSize['2xl'],
    fontWeight: theme.fontWeight.bold,
    textAlign: 'center',
  },
  errorMessage: {
    color: theme.colors.typographyMuted,
    fontSize: theme.fontSize.md,
    fontWeight: theme.fontWeight.normal,
    textAlign: 'center',
  },
  errorButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.full,
    paddingHorizontal: theme.space('2xl'),
    paddingVertical: theme.space('lg'),
    _web: {
      _hover: {
        backgroundColor: theme.colors.primaryHover,
      },
    },
  },
  errorButtonText: {
    color: theme.colors.white,
    fontSize: theme.fontSize.md,
    fontWeight: theme.fontWeight.semibold,
  },
}));
